<template>
  <div class="step-content py-4 p-0 pt-1 p-md-4">
    <h3 class="step-title">Dados Pessoais</h3>
    <p class="step-description">Informe seus dados pessoais para cadastrar sua chave Pix de recebimento.</p>

    <!-- Nome completo -->
    <div class="mb-3">
      <label for="s1FullName" class="form-label">Nome Completo</label>
      <input
        type="text"
        id="s1FullName"
        class="form-control register-input"
        :class="{ 'is-invalid': errors.fullName || props.gatewayErrors.full_name }"
        v-model="fullName"
        placeholder="Ex: João da Silva"
        @input="errors.fullName = ''"
      />
      <div v-if="errors.fullName || props.gatewayErrors.full_name" class="text-danger mt-1">
        {{ errors.fullName || props.gatewayErrors.full_name }}
      </div>
    </div>

    <div class="row gx-2 mb-0">
      <!-- E-mail -->
      <div class="col-12 col-md-6 mb-3">
        <label class="form-label">
          E-mail
          <span v-if="!emailUnlocked" class="badge-locked">
            <i class="fas fa-lock"></i> Definido no cadastro
          </span>
          <span v-else class="badge-unlocked">
            <i class="fas fa-exclamation-triangle"></i> Corrija este campo
          </span>
        </label>
        <input
          id="s1Email"
          v-model="email"
          type="email"
          class="form-control"
          :class="{
            'field-locked':   !emailUnlocked,
            'field-unlocked': emailUnlocked,
            'is-invalid':     !!(errors.email || props.gatewayErrors?.email),
          }"
          :disabled="!emailUnlocked"
          :title="emailUnlocked ? '' : 'O e-mail não pode ser alterado aqui'"
          placeholder="seu@email.com"
          @input="errors.email = ''; clearGatewayError('email')"
        />
        <div v-if="errors.email || props.gatewayErrors?.email" class="text-danger mt-1">
          {{ errors.email || props.gatewayErrors.email }}
        </div>
      </div>

      <!-- CPF (somente leitura ou editável para usuários OAuth sem CPF) -->
      <div class="col-12 col-md-6 mb-3">
        <label for="s1Cpf" class="form-label">
          CPF
          <span v-if="!cpfEditable" class="badge-locked">
            <i class="fas fa-lock"></i> Definido no cadastro
          </span>
        </label>
        <input
          id="s1Cpf"
          v-model="cpfDocument"
          type="text"
          :class="['form-control', cpfEditable ? 'register-input' : 'field-locked', { 'is-invalid': errors.cpfDocument || props.gatewayErrors.cpf_document }]"
          :disabled="!cpfEditable"
          placeholder="000.000.000-00"
          v-mask="'###.###.###-##'"
          @input="errors.cpfDocument = ''"
        />
        <div v-if="errors.cpfDocument || props.gatewayErrors.cpf_document" class="text-danger mt-1">
          {{ errors.cpfDocument || props.gatewayErrors.cpf_document }}
        </div>
      </div>

      <!-- Celular -->
      <div class="col-12 col-md-6 mb-3">
        <label for="s1Phone" class="form-label">Celular</label>
        <input
          type="tel"
          id="s1Phone"
          class="form-control register-input"
          :class="{ 'is-invalid': errors.phone || props.gatewayErrors.phone }"
          v-model="phone"
          placeholder="(99) 9.9999-9999"
          v-mask="'(##) #.####-####'"
          @input="errors.phone = ''"
        />
        <div v-if="errors.phone || props.gatewayErrors.phone" class="text-danger mt-1">
          {{ errors.phone || props.gatewayErrors.phone }}
        </div>
      </div>

      <!-- Data de nascimento (editável) -->
      <div class="col-12 col-md-6 mb-3">
        <label for="s1BirthDate" class="form-label">Data de nascimento</label>
        <input
          id="s1BirthDate"
          v-model="birthDate"
          type="date"
          class="form-control register-input"
          :class="{ 'is-invalid': errors.birthDate || props.gatewayErrors.birth_date }"
          @input="errors.birthDate = ''"
        />
        <div v-if="errors.birthDate || props.gatewayErrors.birth_date" class="text-danger mt-1">
          {{ errors.birthDate || props.gatewayErrors.birth_date }}
        </div>
      </div>

      <!-- CEP -->
      <div class="col-12 col-md-6 mb-3">
        <label for="s1PostalCode" class="form-label">CEP</label>
        <input
          id="s1PostalCode"
          v-model="postalCode"
          type="text"
          class="form-control register-input"
          :class="{ 'is-invalid': errors.postalCode || props.gatewayErrors.postal_code }"
          v-mask="'#####-###'"
          placeholder="00000-000"
          @input="onPostalCodeInput"
        />
        <div v-if="errors.postalCode || props.gatewayErrors.postal_code" class="text-danger mt-1">
          {{ errors.postalCode || props.gatewayErrors.postal_code }}
        </div>
      </div>

      <!-- Número -->
      <div class="col-12 col-md-6 mb-3">
        <label for="s1AddressNumber" class="form-label">Número</label>
        <input
          id="s1AddressNumber"
          v-model="addressNumber"
          type="text"
          class="form-control register-input"
          :class="{ 'is-invalid': errors.addressNumber || props.gatewayErrors.address_number }"
          @input="errors.addressNumber = ''"
        />
        <div v-if="errors.addressNumber || props.gatewayErrors.address_number" class="text-danger mt-1">
          {{ errors.addressNumber || props.gatewayErrors.address_number }}
        </div>
      </div>

      <!-- Logradouro -->
      <div v-if="showAddressFields" class="col-12 mb-3">
        <label for="s1Address" class="form-label">Logradouro</label>
        <input id="s1Address" v-model="address" type="text" class="form-control field-locked" disabled />
      </div>

      <!-- Bairro -->
      <div v-if="showAddressFields" class="col-12 mb-3">
        <label for="s1Province" class="form-label">Bairro</label>
        <input id="s1Province" v-model="province" type="text" class="form-control field-locked" disabled />
      </div>

      <!-- Cidade -->
      <div v-if="showAddressFields" class="col-12 col-md-8 mb-3">
        <label for="s1City" class="form-label">Cidade</label>
        <input id="s1City" v-model="city" type="text" class="form-control field-locked" disabled />
      </div>

      <!-- UF -->
      <div v-if="showAddressFields" class="col-12 col-md-4 mb-3">
        <label for="s1State" class="form-label">UF</label>
        <input id="s1State" v-model="state" type="text" class="form-control field-locked" disabled />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onBeforeUnmount, onMounted, watch } from 'vue';
import { useAuthStore } from '@/stores/authStore';
import { isValidBRPhone } from '@/utils/phoneValidator';

const props = defineProps({
  gatewayErrors: {
    type: Object,
    default: () => ({}),
  },
  useRegisterData: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['clear-gateway-error']);

const emailUnlocked = computed(() => Object.prototype.hasOwnProperty.call(props.gatewayErrors || {}, 'email'));

const clearGatewayError = (field) => {
  emit('clear-gateway-error', field);
};

const authStore = useAuthStore();

// CPF pode ser editado por usuários OAuth que não preencheram o documento no cadastro
const cpfEditable = computed(() => {
  const user = authStore.user;
  const hasProvider = Boolean(user?.provider);
  const hasDocument = Boolean(String(user?.document || user?.cpf_document || '').trim());
  return hasProvider && !hasDocument;
});

const fullName    = ref('');
const email       = ref('');
const phone       = ref('');
const birthDate   = ref('');
const cpfDocument = ref('');
const postalCode  = ref('');
const addressNumber = ref('');
const address     = ref('');
const province    = ref('');
const city        = ref('');
const state       = ref('');
const errors      = ref({});

const showAddressFields = computed(() => {
  return Boolean(
    String(address.value || '').trim() ||
    String(province.value || '').trim() ||
    String(city.value || '').trim() ||
    String(state.value || '').trim()
  );
});

let cepLookupController = null;
let cepLookupTimer = null;

const onlyDigits = (value = '') => String(value).replace(/\D/g, '');

const clearAddressFromCep = () => {
  address.value = '';
  province.value = '';
  city.value = '';
  state.value = '';
};

const fetchAddressByPostalCode = async (postalCodeDigits) => {
  if (cepLookupController) {
    cepLookupController.abort();
  }

  cepLookupController = new AbortController();

  try {
    const response = await fetch(`https://brasilapi.com.br/api/cep/v1/${postalCodeDigits}`, {
      signal: cepLookupController.signal,
    });

    if (!response.ok) {
      throw new Error('CEP não encontrado.');
    }

    const data = await response.json();
    address.value = data.street || '';
    province.value = data.neighborhood || '';
    city.value = data.city || '';
    state.value = String(data.state || '').toUpperCase();
    errors.value.postalCode = '';
  } catch (error) {
    if (error?.name === 'AbortError') {
      return;
    }

    clearAddressFromCep();
    errors.value.postalCode = 'Não foi possível localizar esse CEP.';
  }
};

const onPostalCodeInput = () => {
  errors.value.postalCode = '';
  const digits = onlyDigits(postalCode.value);

  if (cepLookupTimer) {
    clearTimeout(cepLookupTimer);
    cepLookupTimer = null;
  }

  if (digits.length !== 8) {
    clearAddressFromCep();
    return;
  }

  cepLookupTimer = setTimeout(() => {
    fetchAddressByPostalCode(digits);
  }, 350);
};

const focusFirstInvalidField = async () => {
  await nextTick();

  const fieldOrder = [
    ['fullName', 's1FullName'],
    ['email', 's1Email'],
    ['cpfDocument', 's1Cpf'],
    ['phone', 's1Phone'],
    ['birthDate', 's1BirthDate'],
    ['postalCode', 's1PostalCode'],
    ['addressNumber', 's1AddressNumber'],
  ];

  for (const [errorField, elementId] of fieldOrder) {
    if (errors.value[errorField]) {
      const element = document.getElementById(elementId);
      if (element) {
        element.focus();
      }
      break;
    }
  }
};

const validateAndProceed = () => {
  errors.value = {};
  let isValid = true;

  if (!fullName.value.trim() || fullName.value.trim().length < 3) {
    errors.value.fullName = 'Informe o nome completo (mínimo 3 caracteres).';
    isValid = false;
  }

  if (emailUnlocked.value) {
    if (!email.value.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim())) {
      errors.value.email = 'Informe um e-mail válido.';
      isValid = false;
    }
  }

  if (cpfEditable.value) {
    const cpfDigits = onlyDigits(cpfDocument.value);
    if (!cpfDigits) {
      errors.value.cpfDocument = 'Informe seu CPF.';
      isValid = false;
    } else if (cpfDigits.length !== 11) {
      errors.value.cpfDocument = 'Informe um CPF válido com 11 dígitos.';
      isValid = false;
    }
  }

  const cleanPhone = phone.value.replace(/\D/g, '');
  if (!cleanPhone) {
    errors.value.phone = 'Informe um celular válido com DDD.';
    isValid = false;
  } else if (!isValidBRPhone(cleanPhone)) {
    errors.value.phone = 'Informe um celular válido com DDD (ex: (11) 98765-4321).';
    isValid = false;
  }

  if (!String(birthDate.value || '').trim()) {
    errors.value.birthDate = 'Informe sua data de nascimento.';
    isValid = false;
  }

  if (onlyDigits(postalCode.value).length !== 8) {
    errors.value.postalCode = 'Informe um CEP válido com 8 dígitos.';
    isValid = false;
  }

  if (!String(addressNumber.value).trim()) {
    errors.value.addressNumber = 'Informe o número do endereço.';
    isValid = false;
  }

  if (!String(address.value).trim() || !String(city.value).trim() || String(state.value).trim().length !== 2) {
    errors.value.postalCode = errors.value.postalCode || 'Preencha um CEP válido para carregar o endereço.';
    isValid = false;
  }

  if (!isValid) {
    focusFirstInvalidField();
    return { isValid: false, message: 'Verifique os campos de Dados Pessoais.' };
  }

  return {
    isValid: true,
    data: {
      full_name:    fullName.value.trim(),
      email:        email.value.trim(),
      phone:        phone.value,
      birth_date:   birthDate.value,
      cpf_document: cpfDocument.value,
      postal_code:  onlyDigits(postalCode.value),
      address_number: String(addressNumber.value).trim(),
      address:      String(address.value).trim(),
      province:     String(province.value).trim(),
      city:         String(city.value).trim(),
      state:        String(state.value).trim().toUpperCase(),
    },
  };
};

const applyFormData = (data = {}) => {
  if (!data || typeof data !== 'object') return;

  if (typeof data.full_name === 'string') fullName.value = data.full_name;
  if (typeof data.email === 'string') email.value = data.email;
  if (typeof data.phone === 'string') phone.value = data.phone;
  if (typeof data.birth_date === 'string') birthDate.value = data.birth_date;
  if (typeof data.cpf_document === 'string') cpfDocument.value = data.cpf_document;
  if (typeof data.postal_code === 'string') postalCode.value = data.postal_code;
  if (typeof data.address_number === 'string') addressNumber.value = data.address_number;
  if (typeof data.address === 'string') address.value = data.address;
  if (typeof data.province === 'string') province.value = data.province;
  if (typeof data.city === 'string') city.value = data.city;
  if (typeof data.state === 'string') state.value = data.state;
};

const fillIfEmpty = (targetRef, value) => {
  if (String(targetRef.value || '').trim()) {
    return;
  }

  const normalized = value == null ? '' : String(value);
  if (!normalized.trim()) {
    return;
  }

  targetRef.value = normalized;
};

const hydrateMissingFieldsFromUser = (user) => {
  if (!user || typeof user !== 'object') {
    return;
  }

  fillIfEmpty(fullName, user.fullName || user.name || '');
  fillIfEmpty(email, user.email || '');
  fillIfEmpty(phone, user.phone || '');
  fillIfEmpty(birthDate, user.birth_date || '');
  fillIfEmpty(cpfDocument, user.cpf_document || user.document || '');
  fillIfEmpty(postalCode, user.postal_code || '');
  fillIfEmpty(addressNumber, user.address_number || '');
  fillIfEmpty(address, user.address || '');
  fillIfEmpty(province, user.province || '');
  fillIfEmpty(city, user.city || '');
  fillIfEmpty(state, (user.state || '').toUpperCase());
};

defineExpose({ validateAndProceed, applyFormData });

onMounted(() => {
  if (props.useRegisterData) {
    const raw = sessionStorage.getItem('register_data');
    if (raw) {
      try {
        const rd = JSON.parse(raw);
        const authEmail = String(authStore.user?.email || '').trim().toLowerCase();
        const registerEmail = String(rd?.email || '').trim().toLowerCase();
        const canUseRegisterData = !authEmail || !registerEmail || authEmail === registerEmail;

        if (canUseRegisterData) {
          fullName.value    = rd.name || '';
          email.value       = rd.email || '';
          phone.value       = rd.phone || '';
        }
      } catch {
        // fallback para authStore
      }
    }
  }

  hydrateMissingFieldsFromUser(authStore.user);
});

watch(
  () => authStore.user,
  (user) => {
    hydrateMissingFieldsFromUser(user);
  },
  { deep: true, immediate: true }
);

onBeforeUnmount(() => {
  if (cepLookupTimer) {
    clearTimeout(cepLookupTimer);
  }

  if (cepLookupController) {
    cepLookupController.abort();
  }
});
</script>

<style scoped>
.step-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #32355d;
  margin-bottom: 0.25rem;
}
.step-description {
  font-size: 0.95rem;
  color: #6c757d;
  margin-bottom: 1.5rem;
}
.form-label {
  font-weight: 600;
  color: #32355d;
  margin-bottom: 0.5rem;
  display: block;
}
.form-control,
.form-select {
  border-radius: 0.75rem;
  min-height: 3.35rem;
  padding: 0.9rem 1rem;
  border: 1px solid #ced4da;
  font-size: 1rem;
  line-height: 1.25;
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}
.form-control:focus,
.form-select:focus {
  border-color: #ff50a3;
  box-shadow: 0 0 0 0.25rem rgba(255, 80, 163, 0.25);
  outline: none;
}
.text-danger {
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.field-locked {
  background-color: #f5f5f5 !important;
  color: #888 !important;
  border-color: #e0e0e0 !important;
  cursor: not-allowed;
}

.badge-locked {
  font-size: 0.72rem;
  font-weight: 500;
  color: #aaa;
  background: #f0f0f0;
  border-radius: 0.4rem;
  padding: 0.1rem 0.4rem;
  margin-left: 0.4rem;
  vertical-align: middle;
}

.badge-locked i {
  font-size: 0.65rem;
}

.register-input {
  background-color: #fafafa;
  border: 1.5px solid #ebebeb;
  border-radius: 0.75rem;
  min-height: 3.35rem;
  padding: 0.9rem 1rem;
  font-size: 1rem;
  color: #32355d;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.register-input:focus {
  background-color: #fff;
  border-color: #ff50a3;
  box-shadow: 0 0 0 3px rgba(255, 80, 163, 0.12);
  outline: none;
}

.register-input::placeholder {
  color: #c0c0c0;
  font-size: 0.94rem;
}

.field-unlocked {
  background-color: #fff8f0 !important;
  border: 1.5px solid #ff9900 !important;
  border-radius: 0.75rem;
  color: #32355d;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.field-unlocked:focus {
  border-color: #e0185d !important;
  box-shadow: 0 0 0 3px rgba(224, 24, 93, 0.12) !important;
  outline: none;
}

.badge-unlocked {
  font-size: 0.72rem;
  font-weight: 600;
  color: #fff;
  background: #ff9900;
  border-radius: 0.4rem;
  padding: 0.15rem 0.5rem;
  margin-left: 0.4rem;
  vertical-align: middle;
  animation: pulse-badge 1.5s ease-in-out infinite;
}

.badge-unlocked i {
  font-size: 0.65rem;
  margin-right: 0.2rem;
}

@keyframes pulse-badge {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}
</style>
